"use client";
import { useCallback, useContext, useMemo, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import {
  LayoutDashboard,
  FileText,
  List,
  Calendar,
  Users,
  Receipt,
  Settings,
  BarChart3,
  Store,
  ChevronDown,
  ChevronsUpDown,
  Check,
  Sparkles,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { DealershipContext } from "@/app/_components/context/dealership-context";
import { AvatarImage } from "@radix-ui/react-avatar";
import Cookies from "js-cookie";
import { NavFeatureFlags, Permissions } from "@/app/_components/consts";
import {
  defaultDealershipPages,
  salesLogSalesPersonNew,
  newSaleNew,
  usersNewPage,
  settingsV2Page,
  newDueBills,
  leadsPage,
} from "@/app/types/types";
import { usePermissions } from "@/hooks/use-permissions";
import { salesLogNew, deliveryCalendarNew } from "@/app/types/types";
import { IconAnalyze } from "@tabler/icons-react";
import { UserContext } from "@/app/_components/context/user-context";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

// Mock dealerships data

export function AppSidebar() {
  const [open, setOpen] = useState(false);

  const { dealerships, currentDealership, setCurrentDealership, hasFeature } =
    useContext(DealershipContext);

  const { user } = useContext(UserContext);

  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();
  // usePathname excludes the "/v2" basePath, matching the Page path values
  const pathname = usePathname();

  const dealershipPages = useMemo(() => {
    if (!currentDealership) return [];

    const modifiedNavPages = defaultDealershipPages;

    if (
      hasPermission(Permissions.VIEW_SALES_LOG_FULL) ||
      hasPermission(Permissions.VIEW_SALES_LOG_PARTIAL) ||
      hasPermission(Permissions.VIEW_SALES_LOG)
    ) {
      modifiedNavPages["sales-log"] = salesLogNew;
    }

    if (hasFeature(NavFeatureFlags.DELIVERY_CALENDAR_V2)) {
      modifiedNavPages["delivery-calendar"] = deliveryCalendarNew;
    }

    if (hasFeature(NavFeatureFlags.NEW_SALE_V2)) {
      if (hasPermission(Permissions.CREATE_NEW_SALE)) {
        modifiedNavPages["new-sale"] = newSaleNew;
      }
    }

    if (hasFeature(NavFeatureFlags.USERS_V2)) {
      modifiedNavPages["users"] = usersNewPage;
    }

    if (hasFeature(NavFeatureFlags.SETTINGS_V2)) {
      modifiedNavPages["configure"] = settingsV2Page;
    }

    if (
      hasFeature(NavFeatureFlags.DUE_BILLS_V2) &&
      hasPermission(Permissions.ACCESS_DUEBILLS)
    ) {
      modifiedNavPages["duebill"] = newDueBills;
    }

    if (
      hasFeature(NavFeatureFlags.LEADS_V2) &&
      (hasPermission(Permissions.VIEW_LEADS) ||
        hasPermission(Permissions.EDIT_LEADS))
    ) {
      modifiedNavPages["leads"] = leadsPage;
    }

    return Object.values(modifiedNavPages);
  }, [currentDealership, hasFeature, hasPermission]);

  const changeDealership = useCallback(
    (dealershipId: string) => {
      const dealership = dealerships.find(
        (dealership) => dealership.id === dealershipId,
      );

      if (!dealership) {
        return;
      }

      Cookies.set("dealershipId", dealershipId);
      setCurrentDealership(dealership);

      location.reload();
    },
    [dealerships, setCurrentDealership],
  );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className={cn(
                    "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground transition-all",
                    // When the sidebar collapses to icons, tighten padding/spacing and center
                    "group-data-[collapsible=icon]:p-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0",
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center justify-center rounded-lg text-sidebar-primary-foreground p-2.5 w-12 h-12 shrink-0",
                      // Collapsed: small, circular, no inner padding
                      "group-data-[collapsible=icon]:w-9 group-data-[collapsible=icon]:h-9 group-data-[collapsible=icon]:rounded-full group-data-[collapsible=icon]:p-0",
                    )}
                  >
                    <Avatar
                      className={cn(
                        "rounded-lg shrink-0 h-24 w-24",
                        // Collapsed: smaller, circular
                        "group-data-[collapsible=icon]:size-6 group-data-[collapsible=icon]:rounded-full group-data-[collapsible=icon]:h-16 group-data-[collapsible=icon]:w-16",
                      )}
                    >
                      <AvatarImage
                        src={`/v2/images/icon_only.svg`}
                        className="object-cover"
                      />

                      {/* <AvatarFallback className="rounded-lg bg-gray-200 text-gray-600 group-data-[collapsible=icon]:rounded-full">
                        {currentDealership?.name
                          .split(" ")
                          .map((w) => w[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </AvatarFallback> */}
                    </Avatar>
                  </div>

                  {/* Hide text and chevron in collapsed state */}
                  <div className="grid flex-1 text-left text-sm group-data-[collapsible=icon]:hidden">
                    <span className="truncate">{currentDealership?.name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      Dealership
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto group-data-[collapsible=icon]:hidden" />
                </SidebarMenuButton>
              </PopoverTrigger>
              <PopoverContent
                className="w-[--radix-popover-trigger-width] p-0"
                align="start"
              >
                <Command>
                  <CommandInput placeholder="Search dealership..." />
                  <CommandList>
                    <CommandEmpty>No dealership found.</CommandEmpty>
                    <CommandGroup>
                      {dealerships.map((dealership) => (
                        <CommandItem
                          key={dealership.id}
                          value={dealership.id}
                          onSelect={() => {
                            changeDealership(dealership.id);
                            setOpen(false);
                          }}
                        >
                          <Store className="mr-2 size-4" />
                          {dealership.name}
                          {currentDealership?.id === dealership.id && (
                            <Check className="ml-auto size-4" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Application</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {/* CarTracked Engine — conversational sales intelligence.
                  Gated: hidden unless the CARTRACKED_ENGINE feature is on. */}
              {hasFeature(NavFeatureFlags.CARTRACKED_ENGINE) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip="Engine"
                    isActive={pathname === "/engine"}
                  >
                    <Link href="/engine">
                      <Sparkles className="text-primary" />
                      <span>Engine</span>
                      <Badge
                        variant="secondary"
                        className="ml-auto h-5 px-1.5 text-xs"
                      >
                        Beta
                      </Badge>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {dealershipPages
                .filter((page) => {
                  const permCheck =
                    !page.permission ||
                    page.permission.length === 0 ||
                    page.permission.some((perm) => hasPermission(perm));
                  const mobileCheck = !isMobile || page.mobileReady === true;
                  return permCheck && mobileCheck;
                })
                .map((item) => (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.name}
                      isActive={pathname === item.path}
                    >
                      <Link href={item.path}>
                        {item.icon}
                        <span>{item.name}</span>
                        {item.badge && (
                          <Badge
                            variant="secondary"
                            className="ml-auto h-5 px-1.5 text-xs"
                          >
                            {item.badge}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}

              {/* Insights with Dropdown */}
              {!isMobile &&
                hasFeature(NavFeatureFlags.INSIGHTS) &&
                hasPermission("ACCESS_REPORTS") && (
                  <Collapsible
                    defaultOpen={false}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip="Insights">
                          <IconAnalyze />
                          <span>Insights</span>
                          <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={pathname === "/insights/cancellations"}
                            >
                              <Link href="/insights/cancellations">
                                Cancellation Analysis
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>

                          {hasFeature(NavFeatureFlags.FINANCE_REPORTS) && (
                            <>
                              <SidebarMenuSubItem>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={pathname === "/insights/trades"}
                                >
                                  <Link href="/insights/trades">
                                    Incoming Trade Analysis
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                              <SidebarMenuSubItem>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={pathname === "/insights/finance"}
                                >
                                  <Link href="/insights/finance">
                                    Product Performance
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            </>
                          )}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter
        className={cn(
          "border-t border-t-sidebar-border transition-all",
          // Hide when sidebar is collapsed to icons
          "group-data-[collapsible=icon]:hidden",
        )}
      >
        <div className="flex items-center justify-between p-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>{user?.name}</span>
          </div>
          <div className="text-right leading-tight">
            <p className="font-medium text-foreground">CarTracked v2.0</p>
            <p className="text-muted-foreground">© All Rights Reserved</p>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
